<script setup>
import axios from 'axios'
import { onMounted, reactive, ref } from 'vue'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import isBetween from 'dayjs/plugin/isBetween'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Toast from 'primevue/toast'
import { PencilSquareIcon } from '@heroicons/vue/20/solid'
import Swal from 'sweetalert2'
import AddCancelDialog from './add_cancel_dialog.vue'
import MergeDialog from './merge_dialog.vue'
import { useToast } from 'primevue/usetoast'
import { labelCss } from '@/helpers/monthly_actual_report_status'
import Dialog from 'primevue/dialog'

const toast = useToast()

dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(isBetween)
dayjs.extend(utc)
dayjs.extend(duration)
const props = defineProps({
  actualPeriod: {
    type: Object,
    required: true
  },
  monthlyActualReport: {
    type: Object,
    required: true
  },
  isDisplayedCreator: {
    type: Boolean,
    required: true
  }
})
const data = reactive({
  isLoading: false,
  actualDetails: [],
  isEditMode: false,
  creatorOptions: [],
  selected: [],
  creatorReportUrl: '',
  timePeriods: [],
  totalPrices: {}
})
const layoutData = reactive({
  isDisplayedTableAll: false,
  isDisplayTotal: true,
  isMultiCreators: false,
  isDisplayedOriginal: false,
  isAdjusting: props.monthlyActualReport.status === 'adjusting'
})
const selectedCreator = ref({
  id: 0,
  name: '',
  grandit_cd: '',
  includes_rest: false,
  tax_included: false
})
const timeDropdownList = []
for (let hour = 0; hour <= 23; hour++) {
  ;['00', '15', '30', '45'].forEach((i) => {
    timeDropdownList.push(`${hour.toString().padStart(2, '0')}:${i}`)
  })
}
onMounted(async () => {
  data.isLoading = true
  await fetchActualDetails()
  data.isLoading = false
})
const switchDisplayedTableAll = () => {
  layoutData.isDisplayedTableAll = !layoutData.isDisplayedTableAll
}
const switchDisplayedDetails = () => {
  layoutData.isDisplayedOriginal = !layoutData.isDisplayedOriginal
}
const switchDisplayTotal = () => {
  layoutData.isDisplayTotal = !layoutData.isDisplayTotal
}
const isDisplayedScheduledTilde = (slotProps) => {
  if (layoutData.isDisplayedOriginal) {
    return (
      slotProps.data.scheduled_time_over24.start !== null ||
      slotProps.data.scheduled_time_over24.end !== null
    )
  } else {
    return (
      slotProps.data.modified_actual_details.scheduled_time.start !== null ||
      slotProps.data.modified_actual_details.scheduled_time.end !== null
    )
  }
}
const actualDetailData = (slotProps) => {
  return layoutData.isDisplayedOriginal
    ? slotProps.data
    : slotProps.data.modified_actual_details
}
const textCss = (slotProps, param = null) => {
  if (layoutData.isDisplayedOriginal) return 'text-gray-800 text-sm'
  if (slotProps.data.disabled_at !== null)
    return 'text-gray-400 line-through text-xs'
  if (param === 'actual_time' || param === 'price') {
    return 'font-bold text-[#00f]'
  }

  return ''
}

const cellCss = (slotProps, param = null) => {
  if (layoutData.isDisplayedOriginal) return ''
  if (slotProps.data.disabled_at !== null) return ''
  if (
    [
      'title',
      'scheduled_start_time',
      'scheduled_end_time',
      'price',
      'tax',
      'note'
    ].includes(param)
  ) {
    let original = slotProps.data[param]
    let modified = slotProps.data.modified_actual_details[param]
    if (original !== modified) return 'bg-red-50'
  }
  if (param === 'work_date') {
    let original = formatWorkDate(slotProps.data).format('M/D')
    let modified = formatWorkDate(
      slotProps.data.modified_actual_details
    ).format('M/D')
    if (original !== modified) return 'bg-red-50'
  }
  return ''
}
const formatWorkDate = (detail) => {
  let workDate = dayjs(detail.work_date)
  let startTime = null
  if (detail.scheduled_time?.start) startTime = detail.scheduled_time.start
  else if (detail.actual_time?.start) startTime = detail.actual_time.start
  if (startTime !== null && Number(startTime.substr(0, 2)) < 8)
    workDate = workDate.subtract(1, 'd')
  return workDate
}
const formatOver24 = (start, end) => {
  if (start === null || end === null) return { start: start, end: end }
  let startTime = Number(start.replace(':', ''))
  let endTime = Number(end.replace(':', ''))
  if (startTime === endTime) endTime += 2400
  if (startTime < 800) {
    startTime += 2400
    endTime += 2400
  }
  if (startTime > endTime) endTime += 2400
  return {
    start: formatTimeToString(startTime),
    end: formatTimeToString(endTime)
  }
}
const formatTimeToString = (time) => {
  return (
    String(time).padStart(4, '0').substr(0, 2) +
    ':' +
    String(time).padStart(4, '0').substr(2, 2)
  )
}
const swalMixin = () => {
  return Swal.mixin({
    customClass: {
      confirmButton:
        'group flex items-center justify-center rounded-md border bg-green-400 py-2 pr-4 pl-3 text-sm font-medium text-white shadow-sm border-transparent hover:bg-green-500 sm:w-auto',
      cancelButton:
        'group mr-4 flex items-center justify-center rounded-md border border-transparent bg-gray-600 py-2 pl-3 pr-4 text-sm font-medium text-white shadow-sm sm:w-auto hover:bg-gray-800'
    },
    buttonsStyling: false
  })
}
const switchEditMode = async () => {
  if (data.isEditMode) {
    const modal = await swalMixin().fire({
      title: '',
      text: '修正を保存せず元に戻してよろしいですか？',
      confirmButtonText: 'はい',
      confirmButtonColor: '#40BF6C',
      cancelButtonText: 'キャンセル',
      showCancelButton: true,
      reverseButtons: true,
      focusCancel: true
    })
    if (modal.isConfirmed) {
      location.reload()
    }
  } else {
    data.isEditMode = true
    layoutData.isDisplayedTableAll = false
    data.actualDetails = data.actualDetails.filter(
      (data) => data.disabled_at === null
    )
  }
}
const switchCreator = async () => {
  await fetchActualDetails(selectedCreator.value.id)
}
const fetchActualDetails = async (creatorId = '') => {
  const result = await axios.get(
    `/monthly_actual_reports/${props.monthlyActualReport.id}.json?creator_id=${creatorId}`
  )
  data.actualDetails = result.data.actual_details
  addGroupKeys()
  data.creatorOptions = result.data.creator_options
  data.timePeriods = result.data.time_periods
  data.totalPrices = result.data.total_prices
  layoutData.isMultiCreators = result.data.company_id !== null
  selectedCreator.value = result.data.creator
  if (result.data.company_id === null) {
    data.creatorReportUrl = `/creators/${selectedCreator.value.id}/monthly_actual_reports/${props.monthlyActualReport.id}`
  } else {
    data.creatorReportUrl = `/companies/${result.data.company_id}/monthly_actual_reports/${props.monthlyActualReport.id}`
  }
}

// actualDetails取得時用 結合表示用のデータを追加します
const addGroupKeys = () => {
  data.actualDetails.forEach((detail) => {
    if (detail.grouped_actual_detail) {
      detail.group_key_checkbox = detail.grouped_actual_detail.id
      detail.group_key_tax = detail.grouped_actual_detail.id
      detail.group_key_price = detail.grouped_actual_detail.id
      detail.group_key_use_under24_format = detail.grouped_actual_detail.id
    } else {
      detail.group_key_checkbox = `A${detail.id}`
      detail.group_key_tax = `A${detail.id}`
      detail.group_key_price = `A${detail.id}`
      detail.group_key_use_under24_format = `A${detail.id}`
    }
  })
}

const saveEditActualDetails = async () => {
  const modal = await swalMixin().fire({
    title: '',
    text: '修正を保存してもよろしいですか？',
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.put(
        `/monthly_actual_reports/${props.monthlyActualReport.id}/actual_details`,
        { actual_details: data.actualDetails }
      )
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '',
        text: '登録が完了しました',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      location.reload()
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '保存に失敗しました',
        text: '修正内容をご確認ください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
const disableActualDetail = async (slotPropsData) => {
  const workDate = dayjs(slotPropsData.work_date).format('M/D')
  const modal = await swalMixin().fire({
    title: '',
    text: `${workDate} ${slotPropsData.title}の作業実績を無効化してよろしいですか？`,
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.delete(`/actual_details/${slotPropsData.id}/validness`)
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '',
        text: '作業実績を無効化しました',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      location.reload()
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '無効化に失敗しました',
        text: 'もう一度お試しください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
const enableActualDetail = async (slotPropsData) => {
  const workDate = dayjs(slotPropsData.work_date).format('M/D')
  const modal = await swalMixin().fire({
    title: '',
    text: `${workDate} ${slotPropsData.title}の作業実績を有効化してよろしいですか？`,
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.post(`/actual_details/${slotPropsData.id}/validness`)
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '',
        text: '作業実績を有効化しました',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      location.reload()
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '有効化に失敗しました',
        text: 'もう一度お試しください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
const fixMonthlyActualReport = async () => {
  const modal = await swalMixin().fire({
    title: '',
    text: '作業明細を確定してよろしいですか？',
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.post(
        `/monthly_actual_reports/${props.monthlyActualReport.id}/pdfs`
      )
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '',
        text: '作業明細を確定しPDFを作成しました。',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      location.reload()
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '作業明細の確定に失敗しました',
        text: '登録内容をご確認ください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
const toAdjusting = async () => {
  const modal = await swalMixin().fire({
    title: '',
    text: 'ロック解除してよろしいですか？',
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.put(
        `/monthly_actual_reports/${props.monthlyActualReport.id}`,
        { status: 'adjusting' }
      )
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '',
        text: 'ロックを解除しました。',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      location.reload()
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: 'ロック解除に失敗しました',
        text: 'もう一度お試しください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}

const split = async (id) => {
  const url = `/grouped_actual_details/${id}.json`
  axios.defaults.headers.common['X-CSRF-Token'] = document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute('content')
  data.isLoading = true
  try {
    await axios.delete(url)
    await fetchActualDetails()
    data.isLoading = false
    toast.add({
      severity: 'success',
      summary: '結合を解除しました',
      life: 3000
    })
  } catch (error) {
    console.error('リクエストでエラーが発生しました:', error)
    data.isLoading = false
  }
}

const checked = (e, actualDetail) => {
  if (e.target.checked) {
    data.selected.push(actualDetail)
  } else {
    data.selected = data.selected.filter((item) => item.id !== actualDetail.id)
  }
}
const headerChecked = (e) => {
  if (e.target.checked) {
    data.selected = data.actualDetails.filter(
      (item) => !item.grouped_actual_detail
    )
  } else {
    data.selected = []
  }
}

const mergeSucceed = () => {
  fetchActualDetails()
  data.selected = []
}
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <Toast
    :pt="{
      detail: { class: 'hidden' },
      icon: { class: 'w-6 h-6' },
      closeButton: { class: 'hidden' }
    }"
  />
  <datalist id="data-list" class="hidden">
    <option v-for="time in timeDropdownList" :key="time" :value="time"></option>
  </datalist>
  <div class="flex justify-between">
    <div class="mt-3 flex-none sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <div class="flex-none sm:flex sm:items-center">
          <h1 class="flex items-center text-2xl font-semibold text-gray-800">
            {{ props.actualPeriod.month }}
            {{ props.monthlyActualReport.name }}
            作業明細
            <span
              class="label ml-3 inline-block rounded px-2 py-1 text-xs font-semibold"
              :class="labelCss(props.monthlyActualReport.status).class"
              >{{ labelCss(props.monthlyActualReport.status).name }}</span
            >
            <button
              v-if="props.monthlyActualReport.rejection_message !== null"
              class="group my-2.5 ml-3 flex items-center whitespace-nowrap rounded border border-red-500 bg-white py-1 pr-3 pl-2.5 text-xs font-thin text-red-500 shadow-sm hover:border-transparent hover:bg-red-400 hover:text-white sm:w-auto"
              @click="layoutData.displayMessageDialog = true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="mr-2 h-4 w-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
              <span class="font-semibold">否認メッセージ表示</span>
            </button>
          </h1>
          <Dialog
            v-model:visible="layoutData.displayMessageDialog"
            header="否認メッセージ"
            :modal="true"
            class="w-1/2"
          >
            <div class="whitespace-pre-line rounded-md border p-5">
              {{ props.monthlyActualReport.rejection_message }}
            </div>
            <template #footer>
              <div class="mt-4 flex flex-wrap items-center justify-end">
                <button
                  class="group my-2.5 mr-2 flex items-center whitespace-nowrap rounded-md border border-gray-500 bg-white py-1 pr-3 pl-2.5 text-sm font-medium shadow-sm hover:border-transparent hover:bg-gray-500 hover:text-white sm:w-auto"
                  @click="layoutData.displayMessageDialog = false"
                >
                  <span class="pl-1 text-gray-800 group-hover:text-white"
                    >閉じる</span
                  >
                </button>
              </div>
            </template>
          </Dialog>
        </div>
        <div class="mt-2 flex flex-row">
          <div class="flex items-center text-sm text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-4 w-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
              />
            </svg>
            {{ props.actualPeriod.department }}
          </div>
          <div class="ml-4 flex items-center text-sm text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
              />
            </svg>
            <a
              :href="`/creators/${selectedCreator.id}/schedules`"
              target="_blank"
              class="hover:text-gray-300 hover:underline"
              >月間スケジュール</a
            >
          </div>
          <div class="ml-3 flex items-center text-sm text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008Zm0 2.25h.008v.008H8.25V13.5Zm0 2.25h.008v.008H8.25v-.008Zm0 2.25h.008v.008H8.25V18Zm2.498-6.75h.007v.008h-.007v-.008Zm0 2.25h.007v.008h-.007V13.5Zm0 2.25h.007v.008h-.007v-.008Zm0 2.25h.007v.008h-.007V18Zm2.504-6.75h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V13.5Zm0 2.25h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V18Zm2.498-6.75h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V13.5ZM8.25 6h7.5v2.25h-7.5V6ZM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 0 0 2.25 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0 0 12 2.25Z"
              />
            </svg>
            <a
              :href="data.creatorReportUrl"
              target="_blank"
              class="hover:text-gray-300 hover:underline"
              >作業明細確認画面</a
            >
          </div>
        </div>
      </div>
    </div>
    <div v-if="!data.isEditMode && layoutData.isAdjusting">
      <button
        class="h-10 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
        @click="fixMonthlyActualReport"
      >
        <div class="flex gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="mt-0.5 h-5 w-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375a9 9 0 0 1 9 9v.375M10.125 2.25A3.375 3.375 0 0 1 13.5 5.625v1.5c0 .621.504 1.125 1.125 1.125h1.5a3.375 3.375 0 0 1 3.375 3.375M9 15l2.25 2.25L15 12"
            />
          </svg>
          <div>作業明細を確定</div>
        </div>
      </button>
    </div>
  </div>
  <div class="flex flex-row items-end justify-between">
    <div class="flex flex-row items-end">
      <select
        v-if="!data.isEditMode && layoutData.isMultiCreators"
        v-model="selectedCreator"
        class="h-8 items-center rounded-md border border-gray-300 bg-white px-3.5 font-medium text-gray-800 shadow-sm hover:bg-gray-50 hover:text-gray-800"
        @change="switchCreator"
      >
        <option
          v-for="creator in data.creatorOptions"
          v-show="creator.name"
          :key="creator.id"
          :value="{
            id: creator.id,
            name: creator.name,
            grandit_cd: creator.grandit_cd,
            includes_rest: creator.includes_rest,
            tax_included: creator.tax_included
          }"
        >
          {{ creator.name }}
        </option>
      </select>
      <div v-if="!data.isEditMode" class="mx-2">
        <input
          id="showAllColumns"
          type="checkbox"
          class="text-indigo-600 focus:ring-indigo-500 mt-4 mr-1 h-3 w-3 rounded border-gray-300 underline drop-shadow"
          :checked="layoutData.isDisplayedTableAll"
          @change="switchDisplayedTableAll"
        />
        <label for="showAllColumns" class="text-sm text-gray-800"
          >全項目を表示</label
        >
      </div>
      <div v-if="!data.isEditMode" class="mx-2">
        <input
          id="showImportData"
          type="checkbox"
          class="text-indigo-600 focus:ring-indigo-500 mt-4 mr-1 h-3 w-3 rounded border-gray-300 underline drop-shadow"
          :checked="layoutData.isDisplayedOriginal"
          @change="switchDisplayedDetails"
        />
        <label for="showImportData" class="text-sm text-gray-800"
          >インポート情報を表示</label
        >
      </div>
      <div v-if="!data.isEditMode" class="mx-2">
        <input
          id="displayTotal"
          type="checkbox"
          class="text-indigo-600 focus:ring-indigo-500 mt-4 mr-1 h-3 w-3 rounded border-gray-300 underline drop-shadow"
          :checked="layoutData.isDisplayTotal"
          @change="switchDisplayTotal"
        />
        <label for="displayTotal" class="text-sm text-gray-800"
          >合計を表示</label
        >
      </div>
    </div>
    <div>
      <div v-if="data.isEditMode" class="flex flex-row">
        <button
          class="group mr-2 flex items-center justify-center rounded-md border border-gray-800 bg-white py-2 pr-4 pl-3 text-sm font-medium text-gray-800 shadow-sm hover:text-gray-500 sm:w-auto md:hover:bg-gray-50"
          @click="switchEditMode"
        >
          <div class="flex">保存せずに戻る</div>
        </button>
        <button
          class="group flex items-center justify-center rounded-md border border-transparent border-gray-500 bg-yellow-500 py-2 pr-4 pl-3 text-sm font-medium text-white shadow-sm hover:bg-yellow-600 sm:w-auto"
          @click="saveEditActualDetails"
        >
          <div class="flex">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-5 w-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
            保存
          </div>
        </button>
      </div>
      <div
        v-else-if="layoutData.isAdjusting"
        id="operations"
        class="flex flex-row gap-2"
      >
        <MergeDialog
          :merge-url="`/grouped_actual_details.json`"
          :actual-details="data.selected"
          @post-succeed="mergeSucceed"
        ></MergeDialog>
        <AddCancelDialog
          :cancel-registration-url="`/monthly_actual_reports/${props.monthlyActualReport.id}/actual_details`"
          :actual-period-start-date="props.actualPeriod.start_date"
          :creator-id="selectedCreator.id"
          @post-succeed="fetchActualDetails"
        ></AddCancelDialog>
        <button
          class="group flex items-center justify-center rounded-md border border-gray-800 bg-white py-2 pr-4 pl-3 text-sm font-medium text-gray-800 shadow-sm hover:border-yellow-500 hover:bg-yellow-500 hover:text-white sm:w-auto"
          @click="switchEditMode"
        >
          <div class="flex">
            <PencilSquareIcon class="mr-1 h-5 w-5"></PencilSquareIcon>
            編集モード
          </div>
        </button>
      </div>
      <div v-else>
        <div class="flex flex-col">
          <div class="flex justify-end">
            <button
              class="group flex w-36 items-center justify-center rounded-md border border-gray-800 bg-white py-2 pr-4 pl-3 text-sm font-medium text-gray-800 shadow-sm hover:border-red-400 hover:bg-red-400 hover:text-white"
              @click="toAdjusting"
            >
              <div class="flex gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="mt-0.5 h-4 w-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                  />
                </svg>
                <div>ロック解除</div>
              </div>
            </button>
          </div>
          <div class="mt-1 text-xs text-gray-700">
            ※作業明細を修正するにはロックを解除してください
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="card overflow-auto">
    <DataTable
      :value="data.actualDetails"
      scroll-height="flex"
      class="z-50"
      row-group-mode="rowspan"
      :group-rows-by="[
        'group_key_checkbox',
        'group_key_tax',
        'group_key_price',
        'group_key_use_under24_format'
      ]"
      size="small"
      scrollable
      maximizable
      show-gridlines
      striped-rows
      row-hover
      :pt="{ table: 'h-full text-sm' }"
    >
      <Column
        v-if="!data.isEditMode && layoutData.isAdjusting"
        field="group_key_checkbox"
      >
        <template #header>
          <input
            type="checkbox"
            class="p-checkbox"
            @click="headerChecked($event)"
          />
        </template>
        <template #body="slotProps">
          <input
            v-if="!slotProps.data.grouped_actual_detail"
            type="checkbox"
            class="p-checkbox"
            :checked="
              data.selected.find((item) => item.id === slotProps.data.id)
            "
            @change="checked($event, slotProps.data)"
          />
          <button
            v-else
            class="w-15 group flex items-center justify-center rounded-md border border-gray-800 bg-white p-1 text-xs font-medium text-gray-800 shadow-sm hover:border-gray-400 hover:text-white md:hover:bg-gray-400"
            @click="split(slotProps.data.grouped_actual_detail.id)"
          >
            解除
          </button>
        </template>
      </Column>
      <Column field="workDate" class="no-padding text-center">
        <template #header>
          <span class="flex-1 py-1 text-center">作業日</span>
        </template>
        <template #body="slotProps">
          <div v-if="data.isEditMode" class="flex h-full flex-col px-3 py-1.5">
            <div class="mt-1 ml-2 flex text-left text-gray-400">
              {{
                formatWorkDate(slotProps.data.modified_actual_details).format(
                  'YYYY/MM/DD'
                )
              }}
            </div>
            <input
              v-model="slotProps.data.modified_actual_details.work_date"
              type="date"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border border-gray-300 p-1 shadow-sm"
            />
          </div>
          <div
            v-else
            class="flex h-full items-center justify-center px-3 py-1.5"
            :class="cellCss(slotProps, 'work_date')"
          >
            <span :class="textCss(slotProps, 'work_date')">
              {{ formatWorkDate(actualDetailData(slotProps)).format('M/D') }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="title" header="タイトル" class="no-padding">
        <template #body="slotProps">
          <div
            v-if="data.isEditMode"
            class="flex h-full items-center justify-between px-3 py-1.5"
          >
            <textarea
              v-model="slotProps.data.modified_actual_details.title"
              rows="2"
              cols="50"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border border-gray-300 p-1 shadow-sm"
            />
          </div>
          <div
            v-else
            class="inline-flex h-full w-full items-center px-3 py-1.5"
            :class="cellCss(slotProps, 'title')"
          >
            <span :class="textCss(slotProps, 'title')">
              {{ actualDetailData(slotProps).title }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="scheduledStartTime" class="no-padding">
        <template #header>
          <span class="flex-1 text-center">予定開始時刻</span>
        </template>
        <template #body="slotProps">
          <div v-if="data.isEditMode" class="flex h-full flex-col px-3 py-1.5">
            <div class="mt-1 ml-2 flex text-left text-gray-400">
              {{
                formatOver24(
                  slotProps.data.modified_actual_details.scheduled_time.start,
                  slotProps.data.modified_actual_details.scheduled_time.end
                ).start
              }}
            </div>
            <input
              v-model="
                slotProps.data.modified_actual_details.scheduled_time.start
              "
              type="time"
              step="900"
              list="data-list"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border border-gray-300 p-1 shadow-sm"
            />
          </div>
          <div
            v-else
            class="flex h-full items-center justify-center px-3 py-1.5"
            :class="cellCss(slotProps, 'scheduled_start_time')"
          >
            <span :class="textCss(slotProps, 'scheduled_start_time')">
              {{ actualDetailData(slotProps).scheduled_time_over24.start }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="scheduledTilde">
        <template #body="slotProps">
          <span
            v-if="isDisplayedScheduledTilde(slotProps)"
            class="inline-flex items-center"
            :class="textCss(slotProps)"
          >
            〜
          </span>
        </template>
      </Column>
      <Column field="scheduledEndTime" class="no-padding">
        <template #header>
          <span class="flex-1 text-center">予定終了時刻</span>
        </template>
        <template #body="slotProps">
          <div v-if="data.isEditMode" class="flex h-full flex-col px-3 py-1.5">
            <div class="mt-1 ml-2 flex text-left text-gray-400">
              {{
                formatOver24(
                  slotProps.data.modified_actual_details.scheduled_time.start,
                  slotProps.data.modified_actual_details.scheduled_time.end
                ).end
              }}
            </div>
            <input
              v-model="
                slotProps.data.modified_actual_details.scheduled_time.end
              "
              type="time"
              step="900"
              list="data-list"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border border-gray-300 p-1 shadow-sm"
            />
          </div>
          <div
            v-else
            class="flex h-full items-center justify-center px-3 py-1.5"
            :class="cellCss(slotProps, 'scheduled_end_time')"
          >
            <span :class="textCss(slotProps, 'scheduled_end_time')">
              {{ actualDetailData(slotProps).scheduled_time_over24.end }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="startTime" class="text-center">
        <template #header>
          <span class="flex-1 text-center">実績開始時刻</span>
        </template>
        <template #body="slotProps">
          <div class="flex items-center justify-center">
            <span :class="textCss(slotProps, 'actual_time')">
              {{ slotProps.data.actual_time.start }}
            </span>
          </div>
        </template>
      </Column>
      <Column field="actualTilde">
        <template #body="slotProps">
          <span
            v-if="
              slotProps.data.actual_time.start !== null ||
              slotProps.data.actual_time.end !== null
            "
            class="inline-flex items-center"
            :class="textCss(slotProps, 'actual_time')"
          >
            〜
          </span>
        </template>
      </Column>
      <Column field="endTime" class="text-center">
        <template #header>
          <span class="flex-1 text-center">実績終了時刻</span>
        </template>
        <template #body="slotProps">
          <div class="flex items-center justify-center">
            <span :class="textCss(slotProps, 'actual_time')">
              {{ slotProps.data.actual_time.end }}
            </span>
          </div>
        </template>
      </Column>
      <div v-if="layoutData.isDisplayedTableAll">
        <Column field="includesRest" class="px-2 text-center">
          <template #header>
            <span class="flex-1 text-center">休憩時間</span>
          </template>
          <template #body="slotProps">
            <div class="flex items-center justify-center">
              <span :class="textCss(slotProps)">
                {{ slotProps.data.rest_time }}
              </span>
            </div>
          </template>
        </Column>
      </div>
      <Column field="workTime" class="px-2 text-center">
        <template #header>
          <div class="flex flex-col pl-2 text-center">
            <div>作業時間</div>
            <div class="text-xs">
              {{ selectedCreator.includes_rest ? '(休憩含む)' : '(休憩除く)' }}
            </div>
          </div>
        </template>
        <template #body="slotProps">
          <div class="flex items-center justify-center">
            <span :class="textCss(slotProps)">
              {{ slotProps.data.actual_work_time }}
            </span>
          </div>
        </template>
      </Column>
      <div v-if="layoutData.isDisplayedTableAll">
        <Column field="diffTime" class="px-2 text-center">
          <template #header>
            <span class="flex-1 text-center">予定との差分</span>
          </template>
          <template #body="slotProps">
            <div class="flex items-center justify-center">
              <span :class="textCss(slotProps, 'diff_work_time')">
                {{ slotProps.data.modified_actual_details.diff_work_time }}
              </span>
            </div>
          </template>
        </Column>
      </div>
      <Column field="group_key_price" class="no-padding">
        <template #header>
          <div class="flex flex-col pl-2 text-center">
            <div>金額</div>
            <div class="text-xs">
              {{ selectedCreator.tax_included ? '(税込)' : '(税抜)' }}
            </div>
          </div>
        </template>
        <template #body="slotProps">
          <div
            v-if="data.isEditMode"
            class="flex h-full items-center justify-between px-3 py-1.5"
          >
            <input
              v-if="slotProps.data.grouped_actual_detail"
              v-model="slotProps.data.grouped_actual_detail.price"
              type="number"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-24 rounded-md border border-gray-300 p-1 shadow-sm"
            />
            <input
              v-else
              v-model="slotProps.data.modified_actual_details.price"
              type="number"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-24 rounded-md border border-gray-300 p-1 shadow-sm"
            />
          </div>
          <div
            v-else
            class="flex h-full items-center justify-end px-3 py-1.5"
            :class="cellCss(slotProps, 'price')"
          >
            <template v-if="slotProps.data.grouped_actual_detail">
              <span class="font-bold text-[#00f]">
                {{
                  Number(
                    slotProps.data.grouped_actual_detail.price
                  ).toLocaleString()
                }}
              </span>
            </template>
            <template v-else>
              <span :class="textCss(slotProps, 'price')">
                {{ Number(actualDetailData(slotProps).price).toLocaleString() }}
              </span>
            </template>
          </div>
        </template>
      </Column>
      <Column field="group_key_tax" header="税率" class="no-padding">
        <template #body="slotProps">
          <div
            v-if="data.isEditMode"
            class="flex h-full items-center justify-between px-3 py-1.5"
          >
            <select
              v-if="slotProps.data.grouped_actual_detail"
              v-model="slotProps.data.grouped_actual_detail.tax"
              class="focus:border-indigo-500 focus:ring-indigo-500 h-8 items-center rounded-md border border-gray-300 bg-white px-3.5 shadow-sm"
            >
              <option :value="null"></option>
              <option :value="0">0%</option>
              <option :value="8">8%</option>
              <option :value="10">10%</option>
            </select>
            <select
              v-else
              v-model="slotProps.data.modified_actual_details.tax"
              class="focus:border-indigo-500 focus:ring-indigo-500 h-8 items-center rounded-md border border-gray-300 bg-white px-3.5 shadow-sm"
            >
              <option :value="null"></option>
              <option :value="0">0%</option>
              <option :value="8">8%</option>
              <option :value="10">10%</option>
            </select>
          </div>
          <div
            v-else
            class="flex h-full items-center px-3 py-1.5"
            :class="cellCss(slotProps, 'tax')"
          >
            <template v-if="slotProps.data.grouped_actual_detail">
              {{ slotProps.data.grouped_actual_detail.tax }}%
            </template>
            <template v-else>
              <span class="inline-flex items-center text-gray-800">
                <span :class="textCss(slotProps, 'tax')">
                  {{
                    actualDetailData(slotProps).tax === null
                      ? ''
                      : `${actualDetailData(slotProps).tax}%`
                  }}</span
                >
              </span>
            </template>
          </div>
        </template>
      </Column>
      <Column
        field="group_key_use_under24_format"
        header="当日に変更"
        class="no-padding"
      >
        <template #body="slotProps">
          <div
            v-if="data.isEditMode"
            class="flex h-full items-center justify-between px-3 py-1.5"
          >
            <input
              v-if="slotProps.data.grouped_actual_detail"
              v-model="slotProps.data.grouped_actual_detail.use_under24_format"
              type="checkbox"
              rows="2"
            />
            <input
              v-else
              v-model="
                slotProps.data.modified_actual_details.use_under24_format
              "
              type="checkbox"
              rows="2"
            />
          </div>
          <div
            v-else
            class="flex inline-flex h-full w-full items-center px-3 py-1.5"
            :class="cellCss(slotProps, 'use_under24_format')"
          >
            <div
              v-if="slotProps.data.grouped_actual_detail"
              :class="textCss(slotProps, 'use_under24_format')"
            >
              <svg
                v-if="slotProps.data.grouped_actual_detail.use_under24_format"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                class="h-5 w-5"
              >
                <path
                  fill-rule="evenodd"
                  d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div v-else :class="textCss(slotProps, 'use_under24_format')">
              <svg
                v-if="actualDetailData(slotProps).use_under24_format"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                class="h-5 w-5"
              >
                <path
                  fill-rule="evenodd"
                  d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </template>
      </Column>
      <Column field="note" header="備考" class="no-padding">
        <template #body="slotProps">
          <div
            v-if="data.isEditMode"
            class="flex h-full items-center justify-between px-3 py-1.5"
          >
            <textarea
              v-model="slotProps.data.modified_actual_details.note"
              rows="2"
              class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border border-gray-300 p-1 shadow-sm"
            />
          </div>
          <div
            v-else
            class="inline-flex h-full w-full items-center px-3 py-1.5"
            :class="cellCss(slotProps, 'note')"
          >
            <span :class="textCss(slotProps, 'note')">{{
              actualDetailData(slotProps).note
            }}</span>
          </div>
        </template>
      </Column>
      <Column
        v-if="layoutData.isAdjusting && !data.isEditMode"
        field="disabled_at"
        class="flex items-center"
      >
        <template #body="slotProps">
          <button
            v-if="slotProps.data.disabled_at === null"
            size="small"
            class="w-15 group flex items-center justify-center rounded-md border border-gray-800 bg-white p-1 text-xs font-medium text-gray-800 shadow-sm hover:border-red-400 hover:text-white md:hover:bg-red-400"
            @click="disableActualDetail(slotProps.data)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="h-3 w-3"
            >
              <path
                fill-rule="evenodd"
                d="M6.72 5.66l11.62 11.62A8.25 8.25 0 006.72 5.66zm10.56 12.68L5.66 6.72a8.25 8.25 0 0011.62 11.62zM5.105 5.106c3.807-3.808 9.98-3.808 13.788 0 3.808 3.807 3.808 9.98 0 13.788-3.807 3.808-9.98 3.808-13.788 0-3.808-3.807-3.808-9.98 0-13.788z"
                clip-rule="evenodd"
              />
            </svg>
            無効
          </button>
          <button
            v-else
            size="small"
            class="w-15 group flex items-center justify-center rounded-md border border-gray-800 bg-white p-1 text-xs font-medium text-gray-800 shadow-sm hover:border-green-400 hover:text-white md:hover:bg-green-400"
            @click="enableActualDetail(slotProps.data)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w3 h-3"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            有効
          </button>
        </template>
      </Column>
    </DataTable>
  </div>
  <div
    v-show="layoutData.isDisplayTotal && !data.isEditMode"
    class="flex items-start justify-end"
  >
    <p v-show="data.timePeriods.length > 0" class="p-2 text-xs text-gray-500">
      結合した作業は10Hとしてカウントされます
    </p>
    <table v-show="data.timePeriods.length > 0" class="text-sm">
      <tbody class="border">
        <tr
          v-for="timePeriod in data.timePeriods"
          :key="timePeriod.name"
          class="bg-white"
        >
          <td class="border bg-gray-100 px-6 py-3">{{ timePeriod.name }}</td>
          <td class="border px-6 py-3">¥{{ timePeriod.price }}</td>
          <td class="border px-6 py-3">{{ timePeriod.count }}</td>
        </tr>
      </tbody>
    </table>
    <table class="mx-2 text-sm">
      <tbody class="border">
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">実績合計</td>
          <td class="border px-6 py-3">{{ data.totalPrices.work_time }}</td>
        </tr>
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">キャンセル料</td>
          <td class="border px-6 py-3">
            <template v-if="data.totalPrices.each_tax">
              ¥{{
                Number(
                  data.totalPrices.each_tax['0'].sub_price
                ).toLocaleString()
              }}
            </template>
          </td>
        </tr>
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">金額合計（税抜）</td>
          <td class="border px-6 py-3">
            ¥{{ Number(data.totalPrices.sub_price).toLocaleString() }}
          </td>
        </tr>
        <tr class="bg-white">
          <td class="border bg-gray-100 px-6 py-3">金額合計（税込）</td>
          <td class="border px-6 py-3">
            ¥{{ Number(data.totalPrices.total_price).toLocaleString() }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-checkbox-box) {
  border: 1px solid #cbd5e1;
  width: 1.25rem;
  height: 1.25rem;
}

:deep(.p-checkbox) {
  width: 1.25rem;
  height: 1.25rem;
}

:deep(.p-datatable-table) {
  border-width: 1.5px;
  border-color: rgb(215, 215, 215);
}

:deep(.p-datatable-tbody > tr > td.no-padding) {
  padding: 0;
}
</style>
